/**
 * AutomationRuleRowContainer — binds one panel rule's controls to its identity
 * (SE-5506).
 *
 * The panel owns the CRUD actions and passes down rule-addressed callbacks; this
 * container curries them to THIS rule via `useCallback`, and derives the
 * plain-language summary by reusing the SAME preview function the builder uses
 * (rule → form → preview) so the panel and the builder never drift in how a
 * rule reads.
 * @remarks Container component — owns the per-rule handler binding + summary.
 */

import { memo, useCallback, useMemo } from "react";

import type { PipelineAutomationRuleFragmentFragment } from "@/generated/graphql";

import type { AutomationRulePreviewLookups } from "../../../utils/automationRulePreview";
import { buildAutomationRulePreview } from "../../../utils/automationRulePreview";
import { ruleToForm } from "../../../utils/automationRuleForm";
import AutomationRuleRowView from "./AutomationRuleRowView";

/** Props for {@link AutomationRuleRowContainer}. */
export interface AutomationRuleRowContainerProps {
  /** The rule to render. */
  readonly rule: PipelineAutomationRuleFragmentFragment;
  /** Name resolvers for the summary line. */
  readonly lookups: AutomationRulePreviewLookups;
  /** Whether a write for this rule is in flight. */
  readonly isBusy: boolean;
  /** Enable/pause handler (rule-addressed). */
  readonly onToggleEnabled: (
    rule: PipelineAutomationRuleFragmentFragment,
    isEnabled: boolean
  ) => void;
  /** Edit handler (rule-addressed). */
  readonly onEdit: (rule: PipelineAutomationRuleFragmentFragment) => void;
  /** Delete handler (rule-addressed). */
  readonly onDelete: (rule: PipelineAutomationRuleFragmentFragment) => void;
}

/**
 * Stable key factory for the (preview-only) form conversion.
 * @param index - Condition index.
 * @returns The stable key.
 */
const previewKey = (index: number): string => String(index);

/**
 * Container for one panel rule row.
 * @param props - {@link AutomationRuleRowContainerProps}
 * @param props.rule - The rule to render
 * @param props.lookups - Name resolvers for the summary line
 * @param props.isBusy - Whether a write for this rule is in flight
 * @param props.onToggleEnabled - Enable/pause handler (rule-addressed)
 * @param props.onEdit - Edit handler (rule-addressed)
 * @param props.onDelete - Delete handler (rule-addressed)
 * @returns The wired {@link AutomationRuleRowView}.
 */
const AutomationRuleRowContainer = ({
  rule,
  lookups,
  isBusy,
  onToggleEnabled,
  onEdit,
  onDelete,
}: AutomationRuleRowContainerProps) => {
  const summary = useMemo(
    () => buildAutomationRulePreview(ruleToForm(rule, previewKey), lookups),
    [rule, lookups]
  );

  const handleToggleEnabled = useCallback(
    (value: boolean) => onToggleEnabled(rule, value),
    [rule, onToggleEnabled]
  );

  const handleEdit = useCallback(() => onEdit(rule), [rule, onEdit]);

  const handleDelete = useCallback(() => onDelete(rule), [rule, onDelete]);

  return (
    <AutomationRuleRowView
      name={rule.name}
      summary={summary}
      isEnabled={rule.isEnabled}
      isBusy={isBusy}
      onToggleEnabled={handleToggleEnabled}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );
};

AutomationRuleRowContainer.displayName = "AutomationRuleRowContainer";

export default memo(AutomationRuleRowContainer);
